"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { facultyService } from "@/services/faculty";

interface FacultyColorsContextType {
  addButtonColor: string;
  cancelButtonColor: string;
  isLoading: boolean;
  isColorSet: boolean;
}

export const FacultyColorsContext = createContext<
  FacultyColorsContextType | undefined
>(undefined);

const DEFAULT_ADD_COLOR = "#0070f3"; // default blue
const DEFAULT_CANCEL_COLOR = "#6b7280"; // gray-500

interface FacultyColorsProviderProps {
  children: ReactNode;
}

export function FacultyColorsProvider({
  children,
}: FacultyColorsProviderProps) {
  const [addButtonColor, setAddButtonColor] =
    useState<string>(DEFAULT_ADD_COLOR);
  const [cancelButtonColor, setCancelButtonColor] =
    useState<string>(DEFAULT_CANCEL_COLOR);
  const [isColorSet, setIsColorSet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load colors from backend on mount
  useEffect(() => {
    const fetchColors = async () => {
      // Only fetch colors if faculty is authenticated
      if (typeof window !== "undefined") {
        const isAuthenticated = localStorage.getItem("facultyAuthenticated");
        if (isAuthenticated !== "true") {
          console.log("🎓 Skipping color fetch - faculty not authenticated");
          setIsLoading(false);
          return;
        }
      }

      try {
        setIsLoading(true);

        console.log("🎓 Fetching faculty institution colors from backend...");

        // Fetch colors from the new faculty endpoint
        const response = await facultyService.getInstitutionColors();

        console.log("🎓 Faculty institution colors response:", response);

        if (response.success === 1 && response.data) {
          const { primary_colour, secondary_colour } = response.data;

          console.log("🎓 Fetched colors:", { primary_colour, secondary_colour });

          if (primary_colour || secondary_colour) {
            if (primary_colour) {
              console.log("🎓 Setting primary color:", primary_colour);
              setAddButtonColor(primary_colour);
            }
            if (secondary_colour) {
              console.log("🎓 Setting secondary color:", secondary_colour);
              setCancelButtonColor(secondary_colour);
            }
            setIsColorSet(true);

            // Sync to local storage for persistence
            if (typeof window !== "undefined") {
              if (primary_colour)
                localStorage.setItem("faculty-add-button-color", primary_colour);
              if (secondary_colour)
                localStorage.setItem(
                  "faculty-cancel-button-color",
                  secondary_colour
                );
            }
            return;
          } else {
            console.log("🎓 Both colors are null, using defaults");
          }
        } else {
          console.log(
            "🎓 Backend response unsuccessful or no data:",
            response
          );
        }
      } catch (error) {
        console.error("❌ Failed to fetch faculty institution colors:", error);
      } finally {
        setIsLoading(false);
      }

      // Fallback to localStorage if backend didn't have colors
      console.log("🎓 Falling back to localStorage");
      if (typeof window !== "undefined") {
        const savedAddColor = localStorage.getItem("faculty-add-button-color");
        const savedCancelColor = localStorage.getItem(
          "faculty-cancel-button-color"
        );

        console.log("🎓 localStorage colors:", { savedAddColor, savedCancelColor });

        if (savedAddColor) {
          setAddButtonColor(savedAddColor);
        }
        if (savedCancelColor) {
          setCancelButtonColor(savedCancelColor);
        }
      }
    };

    fetchColors();
  }, []);

  return (
    <FacultyColorsContext.Provider
      value={{
        addButtonColor,
        cancelButtonColor,
        isLoading,
        isColorSet,
      }}
    >
      {children}
    </FacultyColorsContext.Provider>
  );
}

export const useFacultyColors = () => {
  const context = useContext(FacultyColorsContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      addButtonColor: DEFAULT_ADD_COLOR,
      cancelButtonColor: DEFAULT_CANCEL_COLOR,
      isLoading: false,
      isColorSet: false,
    };
  }
  return context;
};
